import React, { useState, useEffect } from 'react';
import { Button, Card, Row, Col, Form, Spinner } from 'react-bootstrap';
import { PatientServices } from '../../../services/PatientService';

const CataractDetailsCard = ({ 
  patientId, 
  patientDetails, 
  hospitalId, 
  showAlert,
  formatDate,
  convertToApiDateFormat
}) => {
  const [cataractData, setCataractData] = useState({
    type: '', // 'admission' or 'followup'
    dateOfAdmission: '',
    surgery: '', // 'yes' or 'no'
    surgeryDate: '',
    dischargeDate: '',
    reasonForRefusal: '',
    insurance: [], // Array for multi-select
    customInsurance: '', // For when insurance includes "Others"
    followUpDate: '',
    treatmentStatus: '', // 'followUp', 'admission', or 'treatmentCompleted' (for main options)
    admissionTreatmentStatus: '', // 'followUp' or 'treatmentCompleted' (inside admission section)
    remarks: '',
    actualVisitDate: '', // Compulsory for follow-ups
    treatmentCompletionDate: '', // For treatment completion
    treatmentCompletionRemarks: '' // For treatment completion
  });

  // State to store submitted details for read-only display
  const [submittedCataractDetails, setSubmittedCataractDetails] = useState(null);
  
  // State for cataract follow-up details
  const [cataractFollowUpDetails, setCataractFollowUpDetails] = useState([]);

  // Loading state for submit button
  const [submitLoading, setSubmitLoading] = useState(false);

  // Fetch cataract follow-up details
  const fetchCataractFollowUpDetails = async (cataractDetailsId) => {
    try {
      const response = await PatientServices.getCataractFollowUpDetails(cataractDetailsId);
      if (response && response.data) {
        setCataractFollowUpDetails(response.data);
      }
    } catch (error) {
      console.error('Error fetching cataract follow-up details:', error);
      console.error('Error status:', error.response?.status);
      console.error('Error data:', error.response?.data);
      console.error('Error message:', error.message);
    }
  };

  const fetchExistingCataractDetails = async () => {
    try {
      const response = await PatientServices.getPatientCataractDetailsByPatientId(patientId);
      if (response && response.data) {
        setSubmittedCataractDetails(response.data);
        
        // Fetch follow-up details if cataract details exist and eyeProblemStatus is 1 (Follow Up) or 2 (Completed)
        if (response.data.id && response.data.eyeProblemStatus && 
            (response.data.eyeProblemStatus.id === 1 || response.data.eyeProblemStatus.id === 2)) {
          fetchCataractFollowUpDetails(response.data.id);
        }
      }
    } catch (error) {
      console.error('Error fetching existing cataract details:', error);
      console.error('Error status:', error.response?.status);
      console.error('Error data:', error.response?.data);
      console.error('Error message:', error.message);
    }
  };

  useEffect(() => {
    if (patientId) {
      fetchExistingCataractDetails();
    }
  }, [patientId]);


  const handleCataractDataChange = (field, value) => {
    setCataractData((prev) => {
      let updated = { ...prev, [field]: value };
  
      if (field === "type") {
        if (value === "admission") {
          updated = {
            ...updated,
            dateOfAdmission: "",
            surgery: "",
            surgeryDate: "",
            dischargeDate: "",
            reasonForRefusal: "",
            insurance: [],
            followUpPeriod: "",
            treatmentStatus: "",
            admissionTreatmentStatus: "",
            followUpDate: "",
            remarks: "",
            treatmentCompletionDate: "",
            treatmentCompletionRemarks: ""
          };
        } else if (value === "followup") {
          updated = {
            ...updated,
            dateOfAdmission: "",
            surgery: "",
            surgeryDate: "",
            dischargeDate: "",
            reasonForRefusal: "",
            insurance: [],
            followUpPeriod: "",
            treatmentStatus: "",
            admissionTreatmentStatus: "",
            followUpDate: "",
            remarks: "",
            treatmentCompletionDate: "",
            treatmentCompletionRemarks: ""
          };
        }
      }
  
      // Reset fields when surgery is changed
      if (field === "surgery") {
        if (value === "yes") {
          updated = {
            ...updated,
            reasonForRefusal: ""
          };
        } else if (value === "no") {
          updated = {
            ...updated,
            surgeryDate: "",
            dischargeDate: "",
            insurance: [],
            treatmentStatus: "",
            admissionTreatmentStatus: "",
            followUpDate: "",
            remarks: "",
            treatmentCompletionDate: "",
            treatmentCompletionRemarks: ""
          };
        }
      }
  
      // Handle insurance checkboxes
      if (field === "insurance") {
        const currentInsurance = prev.insurance || [];
        if (currentInsurance.includes(value)) {
          updated.insurance = currentInsurance.filter(item => item !== value);
        } else {
          updated.insurance = [...currentInsurance, value];
        }
      }
  
      // Reset fields when treatment status is changed
      if (field === "treatmentStatus") {
        if (value === "treatmentCompleted") {
          updated = {
            ...updated,
            followUpDate: "",
            remarks: ""
          };
        } else if (value === "followUp") {
          updated = {
            ...updated,
            treatmentCompletionDate: "",
            treatmentCompletionRemarks: ""
          };
        }
      }

      // Reset fields when admission treatment status is changed
      if (field === "admissionTreatmentStatus") {
        if (value === "treatmentCompleted") {
          updated = {
            ...updated,
            followUpDate: "",
            remarks: ""
          };
        } else if (value === "followUp") {
          updated = {
            ...updated,
            treatmentCompletionDate: "",
            treatmentCompletionRemarks: ""
          };
        }
      }
  
      return updated;
    });
  };

  // Validation Functions - Separated by use case
  
  // 1. Validate initial admission with surgery
  const validateAdmissionWithSurgery = () => {
    if (!cataractData.dateOfAdmission) {
      showAlert('Validation Error', 'Date of Admission is required.', 'warning');
      return false;
    }
    if (!cataractData.surgeryDate) {
      showAlert('Validation Error', 'Surgery Date is required.', 'warning');
      return false;
    }
    if (!cataractData.dischargeDate) {
      showAlert('Validation Error', 'Discharge Date is required.', 'warning');
      return false;
    }
    if (cataractData.insurance.includes('Others') && !cataractData.customInsurance) {
      showAlert('Validation Error', 'Please specify the insurance details for "Others".', 'warning');
      return false;
    }
    if (!cataractData.admissionTreatmentStatus) {
      showAlert('Validation Error', 'Please select treatment status (Follow Up or Treatment Completed).', 'warning');
      return false;
    }
    if (cataractData.admissionTreatmentStatus === 'followUp') {
      if (!cataractData.followUpDate) {
        showAlert('Validation Error', 'Follow-up date is required.', 'warning');
        return false;
      }
      if (!cataractData.remarks) {
        showAlert('Validation Error', 'Remark is required for follow-up.', 'warning');
        return false;
      }
    }
    if (cataractData.admissionTreatmentStatus === 'treatmentCompleted') {
      if (!cataractData.treatmentCompletionDate) {
        showAlert('Validation Error', 'Treatment completion date is required.', 'warning');
        return false;
      }
      if (!cataractData.treatmentCompletionRemarks) {
        showAlert('Validation Error', 'Treatment completion remarks are required.', 'warning');
        return false;
      }
    }
    return true;
  };

  // 2. Validate admission without surgery (refused)
  const validateAdmissionWithoutSurgery = () => {
    if (!cataractData.reasonForRefusal) {
      showAlert('Validation Error', 'Reason for refusal is required.', 'warning');
      return false;
    }
    return true;
  };

  // 3. Validate initial follow-up type
  const validateInitialFollowUp = () => {
    if (!cataractData.followUpDate) {
      showAlert('Validation Error', 'Follow-up date is required.', 'warning');
      return false;
    }
    if (!cataractData.remarks) {
      showAlert('Validation Error', 'Remark is required.', 'warning');
      return false;
    }
    return true;
  };

  // 4. Validate subsequent follow-up (after admission)
  const validateSubsequentFollowUp = () => {
    console.log('validateSubsequentFollowUp called');
    console.log('actualVisitDate:', cataractData.actualVisitDate);
    console.log('treatmentStatus:', cataractData.treatmentStatus);
    
    if (!cataractData.actualVisitDate) {
      showAlert('Validation Error', 'Actual visit date is required.', 'warning');
      return false;
    }
    if (!cataractData.treatmentStatus) {
      showAlert('Validation Error', 'Please select either Next Follow-up or Treatment Completed.', 'warning');
      return false;
    }
    if (cataractData.treatmentStatus === 'followUp') {
      if (!cataractData.followUpDate) {
        showAlert('Validation Error', 'Follow-up date is required.', 'warning');
        return false;
      }
      if (!cataractData.remarks) {
        showAlert('Validation Error', 'Remark is required for follow-up.', 'warning');
        return false;
      }
    }
    if (cataractData.treatmentStatus === 'treatmentCompleted') {
      if (!cataractData.treatmentCompletionDate) {
        showAlert('Validation Error', 'Treatment completion date is required.', 'warning');
        return false;
      }
      if (!cataractData.treatmentCompletionRemarks) {
        showAlert('Validation Error', 'Treatment completion remarks are required.', 'warning');
        return false;
      }
    }
    return true;
  };

  const handleCataractSubmit = async () => {
    try {
      setSubmitLoading(true);
      
      console.log('=== CATARACT SUBMIT START ===');
      console.log('submittedCataractDetails:', submittedCataractDetails);
      console.log('cataractData:', cataractData);
      
      // NEW CATARACT DETAILS SUBMISSION
      if (!submittedCataractDetails) {
        console.log('CASE: New cataract details submission');
        // Validate type is selected
        if (!cataractData.type) {
          showAlert('Validation Error', 'Please select cataract type (Admission or Require Follow up).', 'warning');
          setSubmitLoading(false);
          return;
        }
        
        // CASE 1: Admission Type
        if (cataractData.type === 'admission') {
          if (!cataractData.surgery) {
            showAlert('Validation Error', 'Please select whether surgery was performed.', 'warning');
            setSubmitLoading(false);
            return;
          }
          
          // CASE 1A: Admission with surgery
          if (cataractData.surgery === 'yes') {
            if (!validateAdmissionWithSurgery()) {
              setSubmitLoading(false);
              return;
            }
          }
          
          // CASE 1B: Admission without surgery
          if (cataractData.surgery === 'no') {
            if (!validateAdmissionWithoutSurgery()) {
              setSubmitLoading(false);
              return;
            }
          }
        }
        
        // CASE 2: Initial Follow-up Type
        if (cataractData.type === 'followup') {
          if (!validateInitialFollowUp()) {
            setSubmitLoading(false);
            return;
          }
        }
      }
      
      // CASE 3: SUBSEQUENT FOLLOW-UP (after admission exists)
      if (submittedCataractDetails && submittedCataractDetails.id && 
          submittedCataractDetails.eyeProblemStatus && submittedCataractDetails.eyeProblemStatus.id === 1) {
        
        console.log('CASE: Subsequent follow-up (after admission)');
        console.log('eyeProblemStatus.id:', submittedCataractDetails.eyeProblemStatus.id);
        
        // CASE 3A: User selected "Admission" option (need to add admission details)
        if (cataractData.treatmentStatus === 'admission') {
          console.log('CASE 3A: Adding admission to existing follow-up');
          
          if (!cataractData.surgery) {
            showAlert('Validation Error', 'Please select whether surgery was performed.', 'warning');
            setSubmitLoading(false);
            return;
          }
          
          if (cataractData.surgery === 'yes') {
            if (!validateAdmissionWithSurgery()) {
              setSubmitLoading(false);
              return;
            }
          }
          
          if (cataractData.surgery === 'no') {
            if (!validateAdmissionWithoutSurgery()) {
              setSubmitLoading(false);
              return;
            }
          }

          // Submit admission data - update the existing follow-up record with admission details
          console.log('Submitting admission data for existing follow-up');
          
          // Prepare insurance array
          let insuranceArray = [...cataractData.insurance];
          if (insuranceArray.includes('Others') && cataractData.customInsurance) {
            insuranceArray = insuranceArray.filter(item => item !== 'Others');
            insuranceArray.push(cataractData.customInsurance);
          }
          const insuranceJson = JSON.stringify(insuranceArray);

          const updateData = {
            id: submittedCataractDetails.id,
            patientId: patientDetails.id,
            hospitalId: hospitalId,
            type: 'admission', // Update type to admission
            surgery: cataractData.surgery,
            dateOfAdmission: cataractData.dateOfAdmission,
            surgeryDate: cataractData.surgeryDate,
            dischargeDate: cataractData.dischargeDate,
            insurance: insuranceJson,
            reasonForRefusal: cataractData.reasonForRefusal,
            actualVisitDate: cataractData.actualVisitDate, // Include actual visit date
            // Include treatment status from admissionTreatmentStatus
            treatmentStatus: cataractData.admissionTreatmentStatus === 'followUp' ? 'followUp' : 
                           cataractData.admissionTreatmentStatus === 'treatmentCompleted' ? 'treatmentCompleted' : '',
            followUpDate: cataractData.admissionTreatmentStatus === 'followUp' ? cataractData.followUpDate : undefined,
            remarks: cataractData.admissionTreatmentStatus === 'followUp' ? cataractData.remarks : undefined,
            treatmentCompletionDate: cataractData.admissionTreatmentStatus === 'treatmentCompleted' ? cataractData.treatmentCompletionDate : undefined,
            treatmentCompletionRemarks: cataractData.admissionTreatmentStatus === 'treatmentCompleted' ? cataractData.treatmentCompletionRemarks : undefined
          };

          console.log('Update data:', updateData);
          const response = await PatientServices.updatePatientCataractDetails(updateData);
          
          if (response && response.data) {
            setSubmittedCataractDetails(response.data.data || response.data);
            await fetchExistingCataractDetails();
            
            // Clear the form
            setCataractData({
              type: '',
              dateOfAdmission: '',
              surgery: '',
              surgeryDate: '',
              dischargeDate: '',
              reasonForRefusal: '',
              insurance: [],
              customInsurance: '',
              followUpDate: '',
              treatmentStatus: '',
              admissionTreatmentStatus: '',
              remarks: '',
              actualVisitDate: '',
              treatmentCompletionDate: '',
              treatmentCompletionRemarks: ''
            });
            
            showAlert('Success', 'Admission details added successfully!', 'success');
            setSubmitLoading(false);
            return;
          }
        }
        // CASE 3B: User selected "Next Follow-up" or "Treatment Completed"
        else {
          if (!validateSubsequentFollowUp()) {
            console.log('Validation failed for subsequent follow-up');
            setSubmitLoading(false);
            return;
          }
          
          console.log('Validation passed for subsequent follow-up');
        }
        
        // If treatment is being marked as completed
        if (cataractData.treatmentStatus === 'treatmentCompleted') {

          const updateData = {
            id: submittedCataractDetails.id,
            type: submittedCataractDetails.type,
            patientId: patientDetails.id,
            hospitalId: submittedCataractDetails.cataractReferredHospital?.id || hospitalId,
            dateOfAdmission: convertToApiDateFormat(submittedCataractDetails.dateOfAdmission),
            surgery: submittedCataractDetails.surgery,
            surgeryDate: convertToApiDateFormat(submittedCataractDetails.surgeryDate),
            insurance: submittedCataractDetails.insurance ? submittedCataractDetails.insurance :  "",
            reasonForRefusal: submittedCataractDetails.reasonForRefusal,
            treatmentStatus: 'treatmentCompleted',
            treatmentCompletionDate: cataractData.treatmentCompletionDate,
            treatmentCompletionRemarks: cataractData.treatmentCompletionRemarks
          };
          
          const response = await PatientServices.updatePatientCataractDetails(updateData);
          if (response && response.data) {
            setSubmittedCataractDetails(response.data.data || response.data);
            // Refresh follow-up details to update the UI
            await fetchExistingCataractDetails();
            // Clear the form
            setCataractData(prev => ({
              ...prev,
              treatmentStatus: '',
              followUpDate: '',
              remarks: '',
              actualVisitDate: '',
              treatmentCompletionDate: '',
              treatmentCompletionRemarks: ''
            }));
            showAlert('Success', 'Treatment marked as completed successfully!', 'success');
          }
        }
        // If adding a new follow-up
        else if (cataractData.treatmentStatus === 'followUp') {
          // Validate follow-up fields
          if (!cataractData.followUpDate) {
            showAlert('Validation Error', 'Follow-up date is required.', 'warning');
            setSubmitLoading(false);
            return;
          }
          if (!cataractData.remarks) {
            showAlert('Validation Error', 'Remark is required for follow-up.', 'warning');
            setSubmitLoading(false);
            return;
          }

          const followUpData = {
            id: submittedCataractDetails.id,
            followUpDate: cataractData.followUpDate,
            actualVisitDate: cataractData.actualVisitDate,
            remarks: cataractData.remarks
          };
          
          const response = await PatientServices.createCataractFollowUp(followUpData);
          if (response && response.data) {
            // Refresh follow-up details
            fetchCataractFollowUpDetails(submittedCataractDetails.id);
            // Clear the input fields
            setCataractData(prev => ({
              ...prev,
              treatmentStatus: '',
              followUpDate: '',
              remarks: '',
              actualVisitDate: ''
            }));
            showAlert('Success', 'Follow-up details added successfully!', 'success');
          }
        }
      } else {
        // This is a new cataract details submission
        // Prepare insurance array - replace "Others" with custom value if provided
        let insuranceArray = [...cataractData.insurance];
        if (insuranceArray.includes('Others') && cataractData.customInsurance) {
          insuranceArray = insuranceArray.filter(item => item !== 'Others');
          insuranceArray.push(cataractData.customInsurance);
        }
        
        // Convert insurance array to JSON string
        const insuranceJson = JSON.stringify(insuranceArray);
        
        // If admission details are filled (dateOfAdmission exists), set type to 'admission' and surgery to 'yes'
        const submissionType = cataractData.dateOfAdmission ? 'admission' : cataractData.type;
        const surgeryValue = cataractData.dateOfAdmission ? 'yes' : cataractData.surgery;
        
        const submitData = {
          patientId: patientDetails.id,
          hospitalId: hospitalId,
          diagnosisType: 'cataract',
          ...cataractData,
          type: submissionType,
          surgery: surgeryValue,
          insurance: insuranceJson,
          // Map admissionTreatmentStatus to treatmentStatus for backend
          treatmentStatus: cataractData.admissionTreatmentStatus || cataractData.treatmentStatus,
          // Include follow-up fields if admissionTreatmentStatus is followUp
          followUpDate: cataractData.admissionTreatmentStatus === 'followUp' ? cataractData.followUpDate : cataractData.followUpDate,
          remarks: cataractData.admissionTreatmentStatus === 'followUp' ? cataractData.remarks : cataractData.remarks,
          // Include completion fields if admissionTreatmentStatus is treatmentCompleted
          treatmentCompletionDate: cataractData.admissionTreatmentStatus === 'treatmentCompleted' ? cataractData.treatmentCompletionDate : cataractData.treatmentCompletionDate,
          treatmentCompletionRemarks: cataractData.admissionTreatmentStatus === 'treatmentCompleted' ? cataractData.treatmentCompletionRemarks : cataractData.treatmentCompletionRemarks
        };
        console.log('Submitting cataract data:', submitData);
        const response = await PatientServices.createPatientCataractDetails(submitData);
        console.log('Submit response:', response);
        console.log('Submit response.data:', response?.data);
        console.log('Submit response.data.data:', response?.data?.data);
        
        // Store the response data for read-only display
        if (response && response.data) {
          const savedData = response.data.data || response.data;
          console.log('Setting submitted details:', savedData);
          setSubmittedCataractDetails(savedData);
          
          // Fetch follow-up details if the record has follow-up status
          if (savedData.id && savedData.eyeProblemStatus && 
              (savedData.eyeProblemStatus.id === 1 || savedData.eyeProblemStatus.id === 2)) {
            await fetchCataractFollowUpDetails(savedData.id);
          }
          
          console.log('Cataract details saved successfully');
          showAlert('Success', 'Cataract details saved successfully!', 'success');
          
          // Clear the form
          setCataractData({
            type: '',
            dateOfAdmission: '',
            surgery: '',
            surgeryDate: '',
            dischargeDate: '',
            reasonForRefusal: '',
            insurance: [],
            customInsurance: '',
            followUpDate: '',
            treatmentStatus: '',
            admissionTreatmentStatus: '',
            remarks: '',
            actualVisitDate: '',
            treatmentCompletionDate: '',
            treatmentCompletionRemarks: ''
          });
        }
      }
    } catch (error) {
      console.error('Error submitting cataract details:', error);
      console.error('Error response:', error.response);
      console.error('Error response data:', error.response?.data);
      console.error('Error response status:', error.response?.status);
      showAlert('Error', error.response?.data?.message || 'Failed to submit cataract details. Please try again.', 'danger');
    } finally {
      setSubmitLoading(false);
    }
  };

  return (
    <Card className="mb-4" style={{ borderTop: '3px solid rgb(31, 23, 184)' }}>
      <Card.Header className="bg-light">
        <h6 className="mb-0" style={{ color: '#083270' }}>Cataract Details</h6>
      </Card.Header>
      <Card.Body>
        {/* Show form only if no submitted details exist */}
        {!submittedCataractDetails && (
          <>
            {/* Type Selection */}
            <Row className="mb-3">
              <Col>
                <Form.Label style={{ fontWeight: 'bold', marginBottom: '10px' }}>Type :</Form.Label>
                <div className="d-flex gap-4">
                  <Form.Check
                    type="radio"
                    id="type-admission"
                    name="cataractType"
                    label="Admission"
                    checked={cataractData.type === 'admission'}
                    onChange={() => handleCataractDataChange('type', 'admission')}
                  />
                  <Form.Check
                    type="radio"
                    id="type-followup"
                    name="cataractType"
                    label="Require Follow up"
                    checked={cataractData.type === 'followup'}
                    onChange={() => handleCataractDataChange('type', 'followup')}
                  />
                </div>
              </Col>
            </Row>

            {/* Admission Type Fields */}
            {cataractData.type === 'admission' && (
              <>
                {/* Surgery Radio Buttons */}
                <Row className="mb-3">
                  <Col>
                    <Form.Label style={{ fontWeight: 'bold', marginBottom: '10px' }}>Surgery :</Form.Label>
                    <div className="d-flex gap-4">
                      <Form.Check
                        type="radio"
                        id="surgery-yes"
                        name="surgery"
                        label="Yes"
                        checked={cataractData.surgery === 'yes'}
                        onChange={() => handleCataractDataChange('surgery', 'yes')}
                      />
                      <Form.Check
                        type="radio"
                        id="surgery-no"
                        name="surgery"
                        label="No"
                        checked={cataractData.surgery === 'no'}
                        onChange={() => handleCataractDataChange('surgery', 'no')}
                      />
                    </div>
                  </Col>
                </Row>

                {/* If surgery = yes → show insurance + surgery date + follow ups */}
                {cataractData.surgery === 'yes' && (
                  <>
                    <Row className="mb-3">

                  <Col md={2}>
                    <Form.Label style={{ fontWeight: 'bold', marginBottom: '10px' }}> Date of Admission <span style={{ color: 'red' }}>*</span> :</Form.Label>
                    <Form.Control
                      type="date"
                      value={cataractData.dateOfAdmission}
                      onChange={(e) =>
                        handleCataractDataChange('dateOfAdmission', e.target.value)
                      }
                      placeholder="MM / DD / YYYY"
                    />
                  </Col>
                      <Col md={2}>
                        <Form.Label style={{ fontWeight: 'bold', marginBottom: '10px' }}>Surgery Date <span style={{ color: 'red' }}>*</span> :</Form.Label>
                        <Form.Control
                          type="date"
                          value={cataractData.surgeryDate}
                          min={cataractData.dateOfAdmission || undefined}
                          onChange={(e) => {
                            const selectedDate = e.target.value;
                            if (cataractData.dateOfAdmission && selectedDate < cataractData.dateOfAdmission) {
                              showAlert('Validation Error', 'Surgery date must be on or after the admission date.', 'warning');
                              return;
                            }
                            handleCataractDataChange('surgeryDate', selectedDate);
                          }}
                          placeholder="MM / DD / YYYY"
                        />
                      </Col>
                      <Col md={2}>
                        <Form.Label style={{ fontWeight: 'bold', marginBottom: '10px' }}>Discharge Date <span style={{ color: 'red' }}>*</span> :</Form.Label>
                        <Form.Control
                          type="date"
                          value={cataractData.dischargeDate}
                          min={cataractData.surgeryDate || cataractData.dateOfAdmission || undefined}
                          onChange={(e) => {
                            const selectedDate = e.target.value;
                            if (cataractData.surgeryDate && selectedDate < cataractData.surgeryDate) {
                              showAlert('Validation Error', 'Discharge date must be on or after the surgery date.', 'warning');
                              return;
                            }
                            handleCataractDataChange('dischargeDate', selectedDate);
                          }}
                          placeholder="MM / DD / YYYY"
                        />
                      </Col>
                      <Col md={6}>
                        <Form.Label style={{ fontWeight: 'bold', marginBottom: '10px' }}>Insurance :</Form.Label>
                        <div className="d-flex flex-wrap gap-3">
                          <Form.Check
                            type="checkbox"
                            id="insurance-bpl"
                            label="BPL"
                            checked={cataractData.insurance?.includes('BPL')}
                            onChange={() => handleCataractDataChange('insurance', 'BPL')}
                          />
                          <Form.Check
                            type="checkbox"
                            id="insurance-yashaswini"
                            label="Yashaswini"
                            checked={cataractData.insurance?.includes('Yashaswini')}
                            onChange={() => handleCataractDataChange('insurance', 'Yashaswini')}
                          />
                          <Form.Check
                            type="checkbox"
                            id="insurance-esi"
                            label="ESI"
                            checked={cataractData.insurance?.includes('ESI')}
                            onChange={() => handleCataractDataChange('insurance', 'ESI')}
                          />
                          <Form.Check
                            type="checkbox"
                            id="insurance-dharmastala"
                            label="Dharmastala"
                            checked={cataractData.insurance?.includes('Dharmastala')}
                            onChange={() => handleCataractDataChange('insurance', 'Dharmastala')}
                          />
                          <Form.Check
                            type="checkbox"
                            id="insurance-bcs"
                            label="BCS"
                            checked={cataractData.insurance?.includes('BCS')}
                            onChange={() => handleCataractDataChange('insurance', 'BCS')}
                          />
                          <Form.Check
                            type="checkbox"
                            id="insurance-others"
                            label="Others"
                            checked={cataractData.insurance?.includes('Others')}
                            onChange={() => handleCataractDataChange('insurance', 'Others')}
                          />
                        </div>
                      </Col>
                      </Row>
                      <Row>

                    </Row>
                    {cataractData.insurance?.includes('Others') && (
                      <Row className="mb-3">
                        <Col md={4}>
                          <Form.Label>Specify Insurance :</Form.Label>
                          <Form.Control
                            type="text"
                            value={cataractData.customInsurance}
                            onChange={(e) => handleCataractDataChange('customInsurance', e.target.value)}
                            placeholder="Enter insurance details"
                          />
                        </Col>
                      </Row>
                    )}
                    <br/>
                    <Row className="mb-3">
                      <Col md={12}>
                        <Form.Label style={{ fontWeight: 'bold' }}>Treatment Status <span style={{ color: 'red' }}>*</span> :</Form.Label>
                        <div className="d-flex gap-4 mt-2">
                          <Form.Check
                            type="radio"
                            id="treatment-followup"
                            name="admissionTreatmentStatus"
                            label="Follow Up"
                            checked={cataractData.admissionTreatmentStatus === 'followUp'}
                            onChange={() => handleCataractDataChange('admissionTreatmentStatus', 'followUp')}
                          />
                          <Form.Check
                            type="radio"
                            id="treatment-completed"
                            name="admissionTreatmentStatus"
                            label="Treatment Completed"
                            checked={cataractData.admissionTreatmentStatus === 'treatmentCompleted'}
                            onChange={() => handleCataractDataChange('admissionTreatmentStatus', 'treatmentCompleted')}
                          />
                        </div>
                      </Col>
                    </Row>

                    {cataractData.admissionTreatmentStatus === 'followUp' && (
                      <Row className="mb-3">
                        <Col md={4}>
                          <Form.Label>Next Follow up Date :</Form.Label>
                          <Form.Control
                            type="date"
                            value={cataractData.followUpDate}
                            min={cataractData.surgeryDate || undefined}
                            onChange={(e) => {
                              const selectedDate = e.target.value;
                              if (cataractData.surgeryDate && selectedDate < cataractData.surgeryDate) {
                                showAlert('Validation Error', 'Follow-up date must be after the surgery date.', 'warning');
                                return;
                              }
                              handleCataractDataChange('followUpDate', selectedDate);
                            }}
                            placeholder="MM / DD / YYYY"
                          />
                        </Col>
                        <Col md={8}>
                          <Form.Label>Remark <span style={{ color: 'red' }}>*</span>:</Form.Label>
                          <Form.Control
                            type="text"
                            value={cataractData.remarks}
                            onChange={(e) =>
                              handleCataractDataChange('remarks', e.target.value)
                            }
                            placeholder="Enter remark"
                            required
                          />
                        </Col>
                      </Row>
                    )}

                    {cataractData.admissionTreatmentStatus === 'treatmentCompleted' && (
                      <Row className="mb-3">
                        <Col md={4}>
                          <Form.Label><span style={{ color: 'red' }}>*</span> Treatment Completed On:</Form.Label>
                          <Form.Control
                            type="date"
                            value={cataractData.treatmentCompletionDate}
                            min={cataractData.dischargeDate || cataractData.surgeryDate || undefined}
                            onChange={(e) => {
                              const selectedDate = e.target.value;
                              const minDate = cataractData.dischargeDate || cataractData.surgeryDate;
                              if (minDate && selectedDate < minDate) {
                                showAlert('Validation Error', 'Treatment completion date must be on or after the discharge or surgery date.', 'warning');
                                return;
                              }
                              handleCataractDataChange('treatmentCompletionDate', selectedDate);
                            }}
                            required
                          />
                        </Col>
                        <Col md={8}>
                          <Form.Label><span style={{ color: 'red' }}>*</span> Treatment Completion Remarks:</Form.Label>
                          <Form.Control
                            type="text"
                            value={cataractData.treatmentCompletionRemarks}
                            onChange={(e) => handleCataractDataChange('treatmentCompletionRemarks', e.target.value)}
                            placeholder="Enter completion remarks"
                            required
                          />
                        </Col>
                      </Row>
                    )}
                  </>
                )}

                {/* If surgery = no → show reason for refusal */}
                {cataractData.surgery === 'no' && (
                  <Row className="mb-3">
                    <Col md={6}>
                      <Form.Label>Reason for Refusal :</Form.Label>
                      <Form.Control
                        as="textarea"
                        rows={3}
                        value={cataractData.reasonForRefusal}
                        onChange={(e) =>
                          handleCataractDataChange('reasonForRefusal', e.target.value)
                        }
                        placeholder="Enter reason for refusal"
                      />
                    </Col>
                  </Row>
                )}
              </>
            )}

            {/* Require Follow up Type Fields */}
            {cataractData.type === 'followup' && (
              <Row className="mb-3">
                <Col md={4}>
                  <Form.Label><span style={{ color: 'red' }}>*</span> Follow-up Date:</Form.Label>
                  <Form.Control
                    type="date"
                    value={cataractData.followUpDate}
                    onChange={(e) =>
                      handleCataractDataChange('followUpDate', e.target.value)
                    }
                    required
                  />
                </Col>
                <Col md={8}>
                  <Form.Label><span style={{ color: 'red' }}>*</span> Remark:</Form.Label>
                  <Form.Control
                    type="text"
                    value={cataractData.remarks}
                    onChange={(e) =>
                      handleCataractDataChange('remarks', e.target.value)
                    }
                    placeholder="Enter remark"
                    required
                  />
                </Col>
              </Row>
            )}

            {/* Submit Button */}
            <Row className="mt-4">
              <Col className="d-flex justify-content-end">
                <Button 
                  variant="primary" 
                  onClick={handleCataractSubmit}
                  disabled={submitLoading}
                  style={{ 
                    padding: '0.5rem 2rem',
                    fontSize: '1rem'
                  }}
                >
                  {submitLoading ? (
                    <>
                      <Spinner
                        as="span"
                        animation="border"
                        size="sm"
                        role="status"
                        aria-hidden="true"
                        className="me-2"
                      />
                      Submitting...
                    </>
                  ) : (
                    'Submit'
                  )}
                </Button>
              </Col>
            </Row>
          </>
        )}

        {/* Read-only Display of Submitted Cataract Details */}
        {submittedCataractDetails && (
          <>
            {/* Display read-only values if dateOfAdmission exists */}
            {submittedCataractDetails.dateOfAdmission && (
              <>
                <Row className="mb-3">
                  <Col md={2}>
                    <Form.Label style={{ fontWeight: 'bold' }}>Surgery:</Form.Label>
                    <Form.Control
                      type="text"
                      value={submittedCataractDetails.surgery || 'N/A'}
                      readOnly
                      style={{ backgroundColor: '#f8f9fa' }}
                    />
                  </Col>

                  <Col md={2}>
                    <Form.Label style={{ fontWeight: 'bold' }}>Date of Admission:</Form.Label>
                    <Form.Control
                      type="text"
                      value={submittedCataractDetails.dateOfAdmission || 'N/A'}
                      readOnly
                      style={{ backgroundColor: '#f8f9fa' }}
                    />
                  </Col>
                  {submittedCataractDetails.surgery === 'no' && submittedCataractDetails.reasonForRefusal && (
                    <Col md={2}>
                      <Form.Label style={{ fontWeight: 'bold' }}>Reason for Refusal:</Form.Label>
                      <Form.Control
                        type="text"
                        value={submittedCataractDetails.reasonForRefusal || 'N/A'}
                        readOnly
                        style={{ backgroundColor: '#f8f9fa' }}
                      />
                    </Col>
                  )}
                </Row>

                {submittedCataractDetails.surgery === 'yes' && (
                  <>
                    <Row className="mb-3">
                      <Col md={2}>
                        <Form.Label style={{ fontWeight: 'bold' }}>Surgery Date:</Form.Label>
                        <Form.Control
                          type="text"
                          value={submittedCataractDetails.surgeryDate || 'N/A'}
                          readOnly
                          style={{ backgroundColor: '#f8f9fa' }}
                        />
                      </Col>
                      <Col md={2}>
                        <Form.Label style={{ fontWeight: 'bold' }}>Discharge Date:</Form.Label>
                        <Form.Control
                          type="text"
                          value={submittedCataractDetails.dischargeDate || 'N/A'}
                          readOnly
                          style={{ backgroundColor: '#f8f9fa' }}
                        />
                      </Col>
                      <Col md={8}>
                        <Form.Label style={{ fontWeight: 'bold' }}>Insurance:</Form.Label>
                        <Form.Control
                          type="text"
                          value={(() => {
                            try {
                              if (!submittedCataractDetails.insurance) return 'N/A';
                              
                              if (typeof submittedCataractDetails.insurance === 'string') {
                                const parsed = JSON.parse(submittedCataractDetails.insurance);
                                return Array.isArray(parsed) && parsed.length > 0 ? parsed.join(', ') : 'N/A';
                              }
                              
                              if (Array.isArray(submittedCataractDetails.insurance)) {
                                return submittedCataractDetails.insurance.length > 0 
                                  ? submittedCataractDetails.insurance.join(', ') 
                                  : 'N/A';
                              }
                              
                              return submittedCataractDetails.insurance;
                            } catch (error) {
                              console.error('Error parsing insurance:', error);
                              return 'N/A';
                            }
                          })()}
                          readOnly
                          style={{ backgroundColor: '#f8f9fa' }}
                        />
                      </Col>
                    </Row>

                    {submittedCataractDetails.eyeProblemStatus && submittedCataractDetails.eyeProblemStatus.id === 2 && (
                      <>
                        {/* Display Treatment Completion Details */}
                        <Row className="mb-3">
                          <Col>
                            <h6 style={{ color: '#28a745', fontWeight: 'bold' }}>
                              ✓ Treatment Completed
                            </h6>
                          </Col>
                        </Row>
                        
                        <Row className="mb-3">
                          <Col md={4}>
                            <Form.Label style={{ fontWeight: 'bold' }}>Treatment Completed On:</Form.Label>
                            <Form.Control
                              type="text"
                              value={submittedCataractDetails.treatmentCompletionDate || 'N/A'}
                              readOnly
                              style={{ backgroundColor: '#f8f9fa' }}
                            />
                          </Col>
                          <Col md={8}>
                            <Form.Label style={{ fontWeight: 'bold' }}>Treatment Completion Remarks:</Form.Label>
                            <Form.Control
                              type="text"
                              value={submittedCataractDetails.treatmentCompletionRemarks || 'N/A'}
                              readOnly
                              style={{ backgroundColor: '#f8f9fa' }}
                            />
                          </Col>
                        </Row>

                        <hr className="my-4" />

                        {/* Display all follow-up history for completed treatment */}
                        {cataractFollowUpDetails && cataractFollowUpDetails.length > 0 && (
                          <>
                            <Row className="mb-3">
                              <Col>
                                <h6 style={{ color: '#083270', fontWeight: 'bold' }}>
                                  Follow-up History
                                </h6>
                              </Col>
                            </Row>
                            {cataractFollowUpDetails.map((followUp, index) => (
                              <Row key={index} className="mb-2">
                                <Col md={3}>
                                  <Form.Label style={{ fontWeight: 'bold' }}>Advised Follow-up Date {index + 1}:</Form.Label>
                                  <Form.Control
                                    type="text"
                                    value={formatDate(followUp.followUpDate)}
                                    readOnly
                                    style={{ backgroundColor: '#f8f9fa' }}
                                  />
                                </Col>
                                <Col md={3}>
                                  <Form.Label style={{ fontWeight: 'bold' }}> Actual Visit Date {index + 1}:</Form.Label>
                                  <Form.Control
                                    type="text"
                                    value={followUp.actualVisitDate ? formatDate(followUp.actualVisitDate) : 'Not recorded'}
                                    readOnly
                                    style={{ backgroundColor: '#f8f9fa' }}
                                  />
                                </Col>
                                <Col md={6}>
                                  <Form.Label style={{ fontWeight: 'bold' }}>Remark:</Form.Label>
                                  <Form.Control
                                    type="text"
                                    value={followUp.remarks || 'N/A'}
                                    readOnly
                                    style={{ backgroundColor: '#f8f9fa' }}
                                  />
                                </Col>
                              </Row>
                            ))}
                          </>
                        )}
                      </>
                    )}

                    {/* Show radio buttons and submit only if status is Follow Up (id=1) */}
                    {submittedCataractDetails.eyeProblemStatus && submittedCataractDetails.eyeProblemStatus.id === 1 && (
                      <>
                        {/* Display all previous follow-up dates */}
                        {cataractFollowUpDetails && cataractFollowUpDetails.length > 0 && (
                          <>
                            <Row className="mb-3">
                              <Col>
                                <h6 style={{ color: '#083270', fontWeight: 'bold' }}>
                                  Previous Follow-up History
                                </h6>
                              </Col>
                            </Row>
                            {cataractFollowUpDetails.map((followUp, index) => (
                              <div key={index}>
                                <Row className="mb-2">
                                  <Col md={followUp.actualVisitDate ? 3 : 4}>
                                    <Form.Label style={{ fontWeight: 'bold' }}>Advised Follow-up Date:</Form.Label>
                                    <Form.Control
                                      type="text"
                                      value={formatDate(followUp.followUpDate)}
                                      readOnly
                                      style={{ backgroundColor: '#f8f9fa' }}
                                    />
                                  </Col>
                                  {followUp.actualVisitDate && (
                                    <Col md={3}>
                                      <Form.Label style={{ fontWeight: 'bold' }}>Actual Visit Date:</Form.Label>
                                      <Form.Control
                                        type="text"
                                        value={formatDate(followUp.actualVisitDate)}
                                        readOnly
                                        style={{ backgroundColor: '#f8f9fa' }}
                                      />
                                    </Col>
                                  )}
                                  <Col md={followUp.actualVisitDate ? 6 : 8}>
                                    <Form.Label style={{ fontWeight: 'bold' }}>Remark:</Form.Label>
                                    <Form.Control
                                      type="text"
                                      value={followUp.remarks || 'N/A'}
                                      readOnly
                                      style={{ backgroundColor: '#f8f9fa' }}
                                    />
                                  </Col>
                                </Row>
                              </div>
                            ))}
                          </>
                        )}

                        <hr className="my-4" />
                        <Row className="mb-3">
                          <Col md={4}>
                            <Form.Label>Actual Visit Date <span style={{ color: 'red' }}>*</span>:</Form.Label>
                            <Form.Control
                              type="date"
                              value={cataractData.actualVisitDate}
                              onChange={(e) =>
                                handleCataractDataChange('actualVisitDate', e.target.value)
                              }
                              required
                            />
                          </Col>
                        </Row>

                        {/* Radio buttons for Next Follow-up or Treatment Completed */}
                        <Row className="mb-3">
                          <Col md={12}>
                            <div className="d-flex gap-4 mt-2">
                              <Form.Check
                                type="radio"
                                id="action-followup"
                                name="followUpAction"
                                label="Next Follow-up"
                                checked={cataractData.treatmentStatus === 'followUp'}
                                onChange={() => handleCataractDataChange('treatmentStatus', 'followUp')}
                              />
                              <Form.Check
                                type="radio"
                                id="action-completed"
                                name="followUpAction"
                                label="Treatment Completed"
                                checked={cataractData.treatmentStatus === 'treatmentCompleted'}
                                onChange={() => handleCataractDataChange('treatmentStatus', 'treatmentCompleted')}
                              />
                            </div>
                          </Col>
                        </Row>

                        {/* Show follow-up date and remarks only if Next Follow-up is selected */}
                        {cataractData.treatmentStatus === 'followUp' && (
                          <Row className="mb-3">
                            <Col md={4}>
                              <Form.Label><span style={{ color: 'red' }}>*</span> Next Follow-up Date:</Form.Label>
                              <Form.Control
                                type="date"
                                value={cataractData.followUpDate}
                                onChange={(e) =>
                                  handleCataractDataChange('followUpDate', e.target.value)
                                }
                                required
                              />
                            </Col>
                            <Col md={8}>
                              <Form.Label><span style={{ color: 'red' }}>*</span> Remark:</Form.Label>
                              <Form.Control
                                type="text"
                                value={cataractData.remarks}
                                onChange={(e) =>
                                  handleCataractDataChange('remarks', e.target.value)
                                }
                                placeholder="Enter remark"
                                required
                              />
                            </Col>
                          </Row>
                        )}

                        {/* Show treatment completion fields if Treatment Completed is selected */}
                        {cataractData.treatmentStatus === 'treatmentCompleted' && (
                          <Row className="mb-3">
                            <Col md={4}>
                              <Form.Label><span style={{ color: 'red' }}>*</span> Treatment Completed On:</Form.Label>
                              <Form.Control
                                type="date"
                                value={cataractData.treatmentCompletionDate}
                                min={cataractData.actualVisitDate || submittedCataractDetails?.dischargeDate || undefined}
                                onChange={(e) => {
                                  const selectedDate = e.target.value;
                                  const minDate = cataractData.actualVisitDate || submittedCataractDetails?.dischargeDate;
                                  if (minDate && selectedDate < minDate) {
                                    showAlert('Validation Error', 'Treatment completion date must be on or after the actual visit date or discharge date.', 'warning');
                                    return;
                                  }
                                  handleCataractDataChange('treatmentCompletionDate', selectedDate);
                                }}
                                required
                              />
                            </Col>
                            <Col md={8}>
                              <Form.Label><span style={{ color: 'red' }}>*</span> Treatment Completion Remarks:</Form.Label>
                              <Form.Control
                                type="text"
                                value={cataractData.treatmentCompletionRemarks}
                                onChange={(e) =>
                                  handleCataractDataChange('treatmentCompletionRemarks', e.target.value)
                                }
                                placeholder="Enter completion remarks"
                                required
                              />
                            </Col>
                          </Row>
                        )}

                        {/* Submit Button */}
                        <Row className="mt-4">
                          <Col className="d-flex justify-content-end">
                            <Button 
                              variant="success" 
                              onClick={handleCataractSubmit}
                              disabled={
                                submitLoading || 
                                !cataractData.actualVisitDate ||
                                !cataractData.treatmentStatus ||
                                (cataractData.treatmentStatus === 'followUp' && (!cataractData.followUpDate || !cataractData.remarks)) ||
                                (cataractData.treatmentStatus === 'treatmentCompleted' && (!cataractData.treatmentCompletionDate || !cataractData.treatmentCompletionRemarks))
                              }
                              style={{ 
                                padding: '0.5rem 2rem',
                                fontSize: '1rem'
                              }}
                            >
                              {submitLoading ? (
                                <>
                                  <Spinner
                                    as="span"
                                    animation="border"
                                    size="sm"
                                    role="status"
                                    aria-hidden="true"
                                    className="me-2"
                                  />
                                  Submitting...
                                </>
                              ) : (
                                cataractData.treatmentStatus === 'treatmentCompleted' ? 'Mark as Completed' : 'Add Follow-up'
                              )}
                            </Button>
                          </Col>
                        </Row>
                      </>
                    )}
                  </>
                )}
              </>
            )}

            {/* Display for "Require Follow up" type */}
            {!submittedCataractDetails.dateOfAdmission && submittedCataractDetails.type === 'followup' && (
              <>
                <Row className="mb-3">
                  <Col md={4}>
                    <Form.Label style={{ fontWeight: 'bold' }}>Type:</Form.Label>
                    <Form.Control
                      type="text"
                      value="Require Follow up"
                      readOnly
                      style={{ backgroundColor: '#f8f9fa' }}
                    />
                  </Col>
                </Row>

                {/* Display all previous follow-up dates */}
                {cataractFollowUpDetails && cataractFollowUpDetails.length > 0 && (
                  <>
                    <Row className="mb-3">
                      <Col>
                        <h6 style={{ color: '#083270', fontWeight: 'bold' }}>
                          {submittedCataractDetails.eyeProblemStatus?.id === 2 ? 'Follow-up History' : 'Previous Follow-up History'}
                        </h6>
                      </Col>
                    </Row>
                    {cataractFollowUpDetails.map((followUp, index) => (
                      <div key={index}>
                        <Row className="mb-2">
                          <Col md={followUp.actualVisitDate ? 3 : 4}>
                            <Form.Label style={{ fontWeight: 'bold' }}>Follow-up Date {index + 1}:</Form.Label>
                            <Form.Control
                              type="text"
                              value={formatDate(followUp.followUpDate)}
                              readOnly
                              style={{ backgroundColor: '#f8f9fa' }}
                            />
                          </Col>
                          {followUp.actualVisitDate && (
                            <Col md={3}>
                              <Form.Label style={{ fontWeight: 'bold' }}>Actual Visit Date:</Form.Label>
                              <Form.Control
                                type="text"
                                value={formatDate(followUp.actualVisitDate)}
                                readOnly
                                style={{ backgroundColor: '#f8f9fa' }}
                              />
                            </Col>
                          )}
                          <Col md={followUp.actualVisitDate ? 6 : 8}>
                            <Form.Label style={{ fontWeight: 'bold' }}>Remark:</Form.Label>
                            <Form.Control
                              type="text"
                              value={followUp.remarks || 'N/A'}
                              readOnly
                              style={{ backgroundColor: '#f8f9fa' }}
                            />
                          </Col>
                        </Row>
                      </div>
                    ))}
                  </>
                )}

                {/* Show radio buttons and submit only if status is Follow Up (id=1) */}
                {submittedCataractDetails.eyeProblemStatus?.id === 1 && (
                  <>
                    <hr className="my-4" />
                    <Row className="mb-3">
                      <Col md={4}>
                        <Form.Label>Actual Visit Date <span style={{ color: 'red' }}>*</span>:</Form.Label>
                        <Form.Control
                          type="date"
                          value={cataractData.actualVisitDate}
                          onChange={(e) =>
                            handleCataractDataChange('actualVisitDate', e.target.value)
                          }
                          required
                        />
                      </Col>
                    </Row>

                    {/* Radio buttons: Next Follow-up, Add Admission, Treatment Completed */}
                    <Row className="mb-3">
                      <Col md={12}>
                        <div className="d-flex gap-4 mt-2">
                          <Form.Check
                            type="radio"
                            id="action-followup-require"
                            name="followUpActionRequire"
                            label="Next Follow-up"
                            checked={cataractData.treatmentStatus === 'followUp'}
                            onChange={() => {
                              handleCataractDataChange('treatmentStatus', 'followUp');
                              // Clear admission fields
                              setCataractData(prev => ({
                                ...prev,
                                treatmentStatus: 'followUp',
                                dateOfAdmission: '',
                                surgeryDate: '',
                                dischargeDate: '',
                                insurance: [],
                                customInsurance: '',
                                treatmentCompletionDate: '',
                                treatmentCompletionRemarks: ''
                              }));
                            }}
                            // style={{ padding: '8px 12px', border: '1px solid #dee2e6', borderRadius: '4px' }}
                          />
                          <Form.Check
                            type="radio"
                            id="action-admission-require"
                            name="followUpActionRequire"
                            label="Admission"
                            checked={cataractData.treatmentStatus === 'admission'}
                            onChange={() => {
                              handleCataractDataChange('treatmentStatus', 'admission');
                              // Clear follow-up and completion fields
                              setCataractData(prev => ({
                                ...prev,
                                treatmentStatus: 'admission',
                                followUpDate: '',
                                remarks: '',
                                treatmentCompletionDate: '',
                                treatmentCompletionRemarks: ''
                              }));
                            }}
                            // style={{ padding: '8px 12px', border: '1px solid #dee2e6', borderRadius: '4px' }}
                          />
                          <Form.Check
                            type="radio"
                            id="action-completed-require"
                            name="followUpActionRequire"
                            label="Treatment Completed"
                            checked={cataractData.treatmentStatus === 'treatmentCompleted'}
                            onChange={() => {
                              handleCataractDataChange('treatmentStatus', 'treatmentCompleted');
                              // Clear admission and follow-up fields
                              setCataractData(prev => ({
                                ...prev,
                                treatmentStatus: 'treatmentCompleted',
                                dateOfAdmission: '',
                                surgeryDate: '',
                                dischargeDate: '',
                                insurance: [],
                                customInsurance: '',
                                followUpDate: '',
                                remarks: ''
                              }));
                            }}
                            // style={{ padding: '8px 12px', border: '1px solid #dee2e6', borderRadius: '4px' }}
                          />
                        </div>
                      </Col>
                    </Row>

                    {/* Show follow-up date and remarks if Next Follow-up is selected */}
                    {cataractData.treatmentStatus === 'followUp' && (
                      <Row className="mb-3">
                        <Col md={4}>
                          <Form.Label><span style={{ color: 'red' }}>*</span> Next Follow-up Date:</Form.Label>
                          <Form.Control
                            type="date"
                            value={cataractData.followUpDate}
                            onChange={(e) =>
                              handleCataractDataChange('followUpDate', e.target.value)
                            }
                            required
                          />
                        </Col>
                        <Col md={8}>
                          <Form.Label><span style={{ color: 'red' }}>*</span> Remark:</Form.Label>
                          <Form.Control
                            type="text"
                            value={cataractData.remarks}
                            onChange={(e) =>
                              handleCataractDataChange('remarks', e.target.value)
                            }
                            placeholder="Enter remark"
                            required
                          />
                        </Col>
                      </Row>
                    )}

                    {/* Show admission form if Add Admission is selected */}
                    {cataractData.treatmentStatus === 'admission' && (
                      <>
                        {/* Surgery Yes/No Selection */}
                        <Row className="mb-3">
                          <Col>
                            <Form.Label style={{ fontWeight: 'bold' }}>Surgery <span style={{ color: 'red' }}>*</span> :</Form.Label>
                            <div className="d-flex gap-4 mt-2">
                              <Form.Check
                                type="radio"
                                id="admission-surgery-yes"
                                name="admissionSurgery"
                                label="Yes"
                                checked={cataractData.surgery === 'yes'}
                                onChange={() => handleCataractDataChange('surgery', 'yes')}
                              />
                              <Form.Check
                                type="radio"
                                id="admission-surgery-no"
                                name="admissionSurgery"
                                label="No"
                                checked={cataractData.surgery === 'no'}
                                onChange={() => handleCataractDataChange('surgery', 'no')}
                              />
                            </div>
                          </Col>
                        </Row>

                        {/* Show surgery-related fields only if surgery = yes */}
                        {cataractData.surgery === 'yes' && (
                          <>
                            <Row className="mb-3">
                              <Col md={3}>
                                <Form.Label><span style={{ color: 'red' }}>*</span> Date of Admission:</Form.Label>
                                <Form.Control
                                  type="date"
                                  value={cataractData.dateOfAdmission}
                                  onChange={(e) => handleCataractDataChange('dateOfAdmission', e.target.value)}
                                />
                              </Col>
                          <Col md={3}>
                            <Form.Label><span style={{ color: 'red' }}>*</span> Surgery Date:</Form.Label>
                            <Form.Control
                              type="date"
                              value={cataractData.surgeryDate}
                              min={cataractData.dateOfAdmission || undefined}
                              onChange={(e) => {
                                const selectedDate = e.target.value;
                                if (cataractData.dateOfAdmission && selectedDate < cataractData.dateOfAdmission) {
                                  showAlert('Validation Error', 'Surgery date must be on or after the admission date.', 'warning');
                                  return;
                                }
                                handleCataractDataChange('surgeryDate', selectedDate);
                              }}
                            />
                          </Col>
                          <Col md={3}>
                            <Form.Label><span style={{ color: 'red' }}>*</span> Discharge Date:</Form.Label>
                            <Form.Control
                              type="date"
                              value={cataractData.dischargeDate}
                              min={cataractData.surgeryDate || cataractData.dateOfAdmission || undefined}
                              onChange={(e) => {
                                const selectedDate = e.target.value;
                                if (cataractData.surgeryDate && selectedDate < cataractData.surgeryDate) {
                                  showAlert('Validation Error', 'Discharge date must be on or after the surgery date.', 'warning');
                                  return;
                                }
                                handleCataractDataChange('dischargeDate', selectedDate);
                              }}
                            />
                          </Col>
                        </Row>

                        <Row className="mb-3">
                          <Col md={12}>
                            <Form.Label>Insurance:</Form.Label>
                            <div className="d-flex flex-wrap gap-3">
                              <Form.Check
                                type="checkbox"
                                id="insurance-bpl-req"
                                label="BPL"
                                checked={cataractData.insurance?.includes('BPL')}
                                onChange={() => handleCataractDataChange('insurance', 'BPL')}
                              />
                              <Form.Check
                                type="checkbox"
                                id="insurance-yashaswini-req"
                                label="Yashaswini"
                                checked={cataractData.insurance?.includes('Yashaswini')}
                                onChange={() => handleCataractDataChange('insurance', 'Yashaswini')}
                              />
                              <Form.Check
                                type="checkbox"
                                id="insurance-esi-req"
                                label="ESI"
                                checked={cataractData.insurance?.includes('ESI')}
                                onChange={() => handleCataractDataChange('insurance', 'ESI')}
                              />
                              <Form.Check
                                type="checkbox"
                                id="insurance-dharmastala-req"
                                label="Dharmastala"
                                checked={cataractData.insurance?.includes('Dharmastala')}
                                onChange={() => handleCataractDataChange('insurance', 'Dharmastala')}
                              />
                              <Form.Check
                                type="checkbox"
                                id="insurance-bcs-req"
                                label="BCS"
                                checked={cataractData.insurance?.includes('BCS')}
                                onChange={() => handleCataractDataChange('insurance', 'BCS')}
                              />
                              <Form.Check
                                type="checkbox"
                                id="insurance-others-req"
                                label="Others"
                                checked={cataractData.insurance?.includes('Others')}
                                onChange={() => handleCataractDataChange('insurance', 'Others')}
                              />
                            </div>
                          </Col>
                        </Row>

                        {cataractData.insurance?.includes('Others') && (
                          <Row className="mb-3">
                            <Col md={4}>
                              <Form.Label>Specify Insurance:</Form.Label>
                              <Form.Control
                                type="text"
                                value={cataractData.customInsurance}
                                onChange={(e) => handleCataractDataChange('customInsurance', e.target.value)}
                                placeholder="Enter insurance details"
                              />
                            </Col>
                          </Row>
                        )}

                            {/* Treatment Status Options after admission details */}
                            <Row className="mb-3">
                          <Col md={12}>
                            <Form.Label style={{ fontWeight: 'bold' }}>Treatment Status <span style={{ color: 'red' }}>*</span> :</Form.Label>
                            <div className="d-flex gap-4 mt-2">
                              <Form.Check
                                type="radio"
                                id="admission-treatment-followup"
                                name="admissionTreatmentStatus"
                                label="Follow Up"
                                checked={cataractData.admissionTreatmentStatus === 'followUp'}
                                onChange={() => handleCataractDataChange('admissionTreatmentStatus', 'followUp')}
                              />
                              <Form.Check
                                type="radio"
                                id="admission-treatment-completed"
                                name="admissionTreatmentStatus"
                                label="Treatment Completed"
                                checked={cataractData.admissionTreatmentStatus === 'treatmentCompleted'}
                                onChange={() => handleCataractDataChange('admissionTreatmentStatus', 'treatmentCompleted')}
                              />
                            </div>
                          </Col>
                        </Row>

                        {/* Follow-up fields if Follow Up is selected INSIDE admission */}
                        {cataractData.admissionTreatmentStatus === 'followUp' && (
                          <Row className="mb-3">
                            <Col md={4}>
                              <Form.Label>Next Follow-up Date:</Form.Label>
                              <Form.Control
                                type="date"
                                value={cataractData.followUpDate}
                                min={cataractData.surgeryDate || cataractData.dateOfAdmission || undefined}
                                onChange={(e) => {
                                  const selectedDate = e.target.value;
                                  const minDate = cataractData.surgeryDate || cataractData.dateOfAdmission;
                                  if (minDate && selectedDate < minDate) {
                                    showAlert('Validation Error', 'Follow-up date must be after the surgery or admission date.', 'warning');
                                    return;
                                  }
                                  handleCataractDataChange('followUpDate', selectedDate);
                                }}
                              />
                            </Col>
                            <Col md={8}>
                              <Form.Label>Remark <span style={{ color: 'red' }}>*</span>:</Form.Label>
                              <Form.Control
                                type="text"
                                value={cataractData.remarks}
                                onChange={(e) => handleCataractDataChange('remarks', e.target.value)}
                                placeholder="Enter remark"
                                required
                              />
                            </Col>
                          </Row>
                        )}

                        {/* Treatment Completion fields if Treatment Completed is selected INSIDE admission */}
                        {cataractData.admissionTreatmentStatus === 'treatmentCompleted' && (
                          <Row className="mb-3">
                            <Col md={4}>
                              <Form.Label><span style={{ color: 'red' }}>*</span> Treatment Completed On:</Form.Label>
                              <Form.Control
                                type="date"
                                value={cataractData.treatmentCompletionDate}
                                min={cataractData.dischargeDate || cataractData.surgeryDate || undefined}
                                onChange={(e) => {
                                  const selectedDate = e.target.value;
                                  const minDate = cataractData.dischargeDate || cataractData.surgeryDate;
                                  if (minDate && selectedDate < minDate) {
                                    showAlert('Validation Error', 'Treatment completion date must be on or after the discharge or surgery date.', 'warning');
                                    return;
                                  }
                                  handleCataractDataChange('treatmentCompletionDate', selectedDate);
                                }}
                                required
                              />
                            </Col>
                            <Col md={8}>
                              <Form.Label><span style={{ color: 'red' }}>*</span> Treatment Completion Remarks:</Form.Label>
                              <Form.Control
                                type="text"
                                value={cataractData.treatmentCompletionRemarks}
                                onChange={(e) => handleCataractDataChange('treatmentCompletionRemarks', e.target.value)}
                                placeholder="Enter completion remarks"
                                required
                              />
                            </Col>
                          </Row>
                        )}
                          </>
                        )}

                        {/* Show reason for refusal if surgery = no */}
                        {cataractData.surgery === 'no' && (
                          <Row className="mb-3">
                            <Col md={6}>
                              <Form.Label><span style={{ color: 'red' }}>*</span> Reason for Refusal:</Form.Label>
                              <Form.Control
                                type="text"
                                value={cataractData.reasonForRefusal}
                                onChange={(e) => handleCataractDataChange('reasonForRefusal', e.target.value)}
                                placeholder="Enter reason for refusal"
                                required
                              />
                            </Col>
                          </Row>
                        )}

                        {/* Submit Admission Button */}
                        <Row className="mt-4">
                          <Col className="d-flex justify-content-end">
                            <Button 
                              variant="success" 
                              onClick={handleCataractSubmit}
                              disabled={submitLoading}
                              style={{ 
                                padding: '0.5rem 2rem',
                                fontSize: '1rem'
                              }}
                            >
                              {submitLoading ? (
                                <>
                                  <Spinner
                                    as="span"
                                    animation="border"
                                    size="sm"
                                    role="status"
                                    aria-hidden="true"
                                    className="me-2"
                                  />
                                  Submitting...
                                </>
                              ) : (
                                'Submit Details'
                              )}
                            </Button>
                          </Col>
                        </Row>
                      </>
                    )}

                    {/* Show treatment completion fields if Treatment Completed is selected */}
                    {cataractData.treatmentStatus === 'treatmentCompleted' && (
                      <>
                        <Row className="mb-3">
                          <Col md={4}>
                            <Form.Label><span style={{ color: 'red' }}>*</span> Treatment Completed On:</Form.Label>
                            <Form.Control
                              type="date"
                              value={cataractData.treatmentCompletionDate}
                              onChange={(e) =>
                                handleCataractDataChange('treatmentCompletionDate', e.target.value)
                              }
                              required
                            />
                          </Col>
                          <Col md={8}>
                            <Form.Label><span style={{ color: 'red' }}>*</span> Treatment Completion Remarks:</Form.Label>
                            <Form.Control
                              type="text"
                              value={cataractData.treatmentCompletionRemarks}
                              onChange={(e) =>
                                handleCataractDataChange('treatmentCompletionRemarks', e.target.value)
                              }
                              placeholder="Enter completion remarks"
                              required
                            />
                          </Col>
                        </Row>

                        {/* Submit Button for Treatment Completion */}
                        <Row className="mt-4">
                          <Col className="d-flex justify-content-end">
                            <Button 
                              variant="success" 
                              onClick={handleCataractSubmit}
                              disabled={submitLoading}
                              style={{ 
                                padding: '0.5rem 2rem',
                                fontSize: '1rem'
                              }}
                            >
                              {submitLoading ? (
                                <>
                                  <Spinner
                                    as="span"
                                    animation="border"
                                    size="sm"
                                    role="status"
                                    aria-hidden="true"
                                    className="me-2"
                                  />
                                  Submitting...
                                </>
                              ) : (
                                'Mark as Completed'
                              )}
                            </Button>
                          </Col>
                        </Row>
                      </>
                    )}

                    {/* Submit Button for Follow-up */}
                    {cataractData.treatmentStatus === 'followUp' && (
                      <Row className="mt-4">
                        <Col className="d-flex justify-content-end">
                          <Button 
                            variant="success" 
                            onClick={handleCataractSubmit}
                            disabled={submitLoading}
                            style={{ 
                              padding: '0.5rem 2rem',
                              fontSize: '1rem'
                            }}
                          >
                            {submitLoading ? (
                              <>
                                <Spinner
                                  as="span"
                                  animation="border"
                                  size="sm"
                                  role="status"
                                  aria-hidden="true"
                                  className="me-2"
                                />
                                Submitting...
                              </>
                            ) : (
                              'Add Follow-up'
                            )}
                          </Button>
                        </Col>
                      </Row>
                    )}
                  </>
                )}
              </>
            )}
          </>
        )}
      </Card.Body>
    </Card>
  );
};

export default CataractDetailsCard;